import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";
import {
  Box,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";

import { GlassOrderStatus } from "../../../enum/GlassOrder";
import {
  GlassOrder,
  GlassOrderFormData,
  GlassPrescriptionData
} from "../../../interfaces/GlassOrder";
import { Client } from "../../../interfaces/ClientInterface";
import {
  useGetGlassOrder,
  usePatchGlassOrder,
  usePostGlassOrder
} from "../../../hooks/query/useGlassOrders";
import Panel from "../../../components/Panel";
import { tl } from "../../../components/translate";
import ClientSearch from "../../../components/ClientSearch";
import { notificationAdd } from "../../../actions/notification";
import { clientSearchSecondaryLabel } from "../../../models/Client";
import StatefulButton from "../../../components/StatefulButton/StatefulButton";
import CalendarDropdown from "../../../components/CalendarDropdown/CalendarDropdown";

interface GlassOrderCreateEditProps {
  glassOrder?: Partial<GlassOrder> | null;
  selectedClient?: Client | null;
  initialPrescription?: GlassPrescriptionData;
  onClose?: () => void;
  onCreateSuccess?: () => void;
  onUpdateSuccess?: () => void;
}

export const PRESCRIPTION_COLS = [
  { key: "sph", label: "SPH" },
  { key: "cyl", label: "CYL" },
  { key: "axis", label: "Axis" },
  { key: "add", label: "ADD" },
  { key: "pd", label: "PD" }
] as const;

export const PRESCRIPTION_ROWS = [
  { key: "OD", label: "OD" },
  { key: "OS", label: "OS" }
] as const;

export const defaultPrescription: GlassPrescriptionData = {
  OD: { sph: "", cyl: "", axis: "", pd: "", add: "" },
  OS: { sph: "", cyl: "", axis: "", pd: "", add: "" }
};

export const parsePrescription = (data: unknown): GlassPrescriptionData => {
  if (!data) return defaultPrescription;
  let parsed = data;
  if (typeof data === "string") {
    try {
      parsed = JSON.parse(data);
    } catch {
      return defaultPrescription;
    }
  }
  const obj = parsed as GlassPrescriptionData;
  return {
    OD: { ...defaultPrescription.OD, ...(obj?.OD || {}) },
    OS: { ...defaultPrescription.OS, ...(obj?.OS || {}) }
  };
};

export default function GlassOrderCreateEdit({
  glassOrder,
  selectedClient,
  initialPrescription,
  onClose,
  onCreateSuccess,
  onUpdateSuccess
}: GlassOrderCreateEditProps): React.JSX.Element {
  const isEdit = !!glassOrder?.id;
  const dispatch = useDispatch();
  const orderId = Number(glassOrder?.id);

  const { data: fetchedOrderRes } = useGetGlassOrder(!glassOrder && orderId ? orderId : null);
  const fetchedOrder = fetchedOrderRes?.data;

  const [client, setClient] = useState<Client | null>(selectedClient || glassOrder?.client || null);

  const [formData, setFormData] = useState<GlassOrderFormData>({
    clientId: selectedClient?.id || glassOrder?.clientId || null,
    orderDate: glassOrder?.orderDate || new Date().toISOString(),
    deliveryDate: glassOrder?.deliveryDate || null,
    prescription: parsePrescription(glassOrder?.prescription || initialPrescription),
    frame: glassOrder?.frame || "",
    lens: glassOrder?.lens || "",
    total: glassOrder?.total ?? 0,
    advance: glassOrder?.advance ?? 0,
    balance: glassOrder?.balance ?? 0,
    remarks: glassOrder?.remarks || "",
    status: (glassOrder?.status as GlassOrderStatus) || GlassOrderStatus.PENDING
  });

  // Sync state if fetched by route parameter
  useEffect(() => {
    if (fetchedOrder && !glassOrder) {
      if (fetchedOrder.client) setClient(fetchedOrder.client);
      setFormData({
        clientId: fetchedOrder.clientId,
        orderDate: fetchedOrder.orderDate || new Date().toISOString(),
        deliveryDate: fetchedOrder.deliveryDate || null,
        prescription: parsePrescription(fetchedOrder.prescription),
        frame: fetchedOrder.frame || "",
        lens: fetchedOrder.lens || "",
        total: fetchedOrder.total ?? 0,
        advance: fetchedOrder.advance ?? 0,
        balance: fetchedOrder.balance ?? 0,
        remarks: fetchedOrder.remarks || "",
        status: (fetchedOrder.status as GlassOrderStatus) || GlassOrderStatus.PENDING
      });
    }
  }, [fetchedOrder, glassOrder]);

  const postMutation = usePostGlassOrder();
  const patchMutation = usePatchGlassOrder();
  const isSaving = postMutation.isLoading || patchMutation.isLoading;

  const handlePrescriptionChange = (rowKey: "OD" | "OS", colKey: string, value: string) => {
    setFormData((prev) => {
      const existingRow = (prev.prescription?.[rowKey] as Record<string, unknown>) || {};
      return {
        ...prev,
        prescription: {
          ...prev.prescription,
          [rowKey]: {
            ...existingRow,
            [colKey]: value
          }
        }
      };
    });
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      dispatch(push("/assessment/glassOrders"));
    }
  };

  const handleSave = async () => {
    const selectedClientId = client?.id || formData.clientId;
    if (!selectedClientId) {
      dispatch(
        notificationAdd({
          id: Date.now(),
          variant: "error",
          message: "Please select a client",
          autoTimeout: true
        })
      );
      return;
    }

    if (!formData.orderDate) {
      dispatch(
        notificationAdd({
          id: Date.now(),
          variant: "error",
          message: "Please select an order date",
          autoTimeout: true
        })
      );
      return;
    }

    const payload: Partial<GlassOrderFormData> = {
      clientId: selectedClientId,
      orderDate: formData.orderDate,
      deliveryDate: formData.deliveryDate || null,
      prescription: formData.prescription,
      frame: formData.frame || null,
      lens: formData.lens || null,
      total: Number(formData.total || 0),
      advance: Number(formData.advance || 0),
      balance: Number(formData.balance || 0),
      remarks: formData.remarks || null,
      status: formData.status
    };

    if (isEdit && (glassOrder?.id || orderId)) {
      const res = await patchMutation.mutateAsync({
        id: (glassOrder?.id || orderId) as number,
        data: payload
      });
      if (res?.success) {
        if (onUpdateSuccess) {
          onUpdateSuccess();
        }
        handleClose();
      }
    } else {
      const res = await postMutation.mutateAsync(payload);
      if (res?.success) {
        if (onCreateSuccess) {
          onCreateSuccess();
        }
        handleClose();
      }
    }
  };

  const cellBorder = { border: 1, borderColor: "divider" };

  return (
    <Panel
      title={isEdit ? "Edit Glass Order" : "Create Glass Order"}
      onClose={handleClose}
      footer={
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            py: 1
          }}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleClose}
            disabled={isSaving}
            sx={{ height: "35px" }}
          >
            {tl("cancel")}
          </Button>
          <StatefulButton
            variant="contained"
            color="primary"
            disabled={isSaving || (!client?.id && !formData.clientId)}
            onClick={handleSave}
            isLoading={isSaving}
            circularProgressProps={{ size: 16 }}
            sx={{ height: "35px", minWidth: "90px" }}
          >
            {isEdit ? tl("update") : tl("save")}
          </StatefulButton>
        </Box>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        {/* Client Selection */}
        <ClientSearch
          data-testmation="glassOrderClientSearch"
          autoFocus={!client && !isEdit}
          client={client}
          setClient={(newClient) => {
            setClient(newClient);
            setFormData((prev) => ({ ...prev, clientId: newClient?.id || null }));
          }}
          variant="outlined"
          margin="dense"
          label="Select Client *"
          placeholder="Search client by name or phone"
          style={{ width: "100%" }}
          isDisabled={isSaving}
          secondaryText={[
            { type: clientSearchSecondaryLabel.CUSTOMER_NUMBER },
            { type: clientSearchSecondaryLabel.EXTERNAL_IDENTIFIER }
          ]}
        />

        {/* Order Date */}
        <Box>
          <CalendarDropdown
            fullwidth
            TextFieldProps={{
              variant: "outlined",
              size: "small",
              InputLabelProps: { shrink: true }
            }}
            label="Order Date *"
            placeholder="Select order date"
            date={formData.orderDate ? new Date(formData.orderDate) : new Date()}
            onChange={(val) => {
              if (val) {
                const d = val instanceof Date ? val : new Date(val);
                if (!Number.isNaN(d.getTime())) {
                  setFormData((prev) => ({ ...prev, orderDate: d.toISOString() }));
                }
              }
            }}
          />
        </Box>

        {/* Prescription Table */}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Glass Prescription
          </Typography>
          <TableContainer>
            <Table size="small" sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: "rgba(0, 0, 0, 0.04)" }}>
                  <TableCell
                    sx={{
                      ...cellBorder,
                      fontWeight: 600,
                      width: "120px"
                    }}
                  >
                    Eye
                  </TableCell>
                  {PRESCRIPTION_COLS.map((col) => (
                    <TableCell
                      key={col.key}
                      sx={{
                        ...cellBorder,
                        fontWeight: 600,
                        textAlign: "center"
                      }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {PRESCRIPTION_ROWS.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell
                      sx={{
                        ...cellBorder,
                        fontWeight: 600,
                        backgroundColor: "rgba(0, 0, 0, 0.02)"
                      }}
                    >
                      {row.label}
                    </TableCell>
                    {PRESCRIPTION_COLS.map((col) => {
                      const rowData =
                        (formData.prescription?.[row.key] as Record<string, unknown>) || {};
                      const val = (rowData[col.key] as string) ?? "";
                      return (
                        <TableCell key={col.key} sx={{ ...cellBorder, p: 0.5 }}>
                          <TextField
                            size="small"
                            variant="outlined"
                            fullWidth
                            value={val}
                            onChange={(e) =>
                              handlePrescriptionChange(row.key, col.key, e.target.value)
                            }
                            sx={{
                              "& .MuiInputBase-input": {
                                padding: "4px 8px",
                                fontSize: "0.85rem",
                                textAlign: "center"
                              }
                            }}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Frame & Lens Details */}
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Box sx={{ flex: 1, minWidth: "200px" }}>
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              label="Frame"
              placeholder="e.g. Ray-Ban RB2132, Rimless, etc."
              value={formData.frame || ""}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => setFormData((prev) => ({ ...prev, frame: e.target.value }))}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: "200px" }}>
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              label="Lens"
              placeholder="e.g. Anti-glare, Blue Cut, Progressive"
              value={formData.lens || ""}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => setFormData((prev) => ({ ...prev, lens: e.target.value }))}
            />
          </Box>
        </Box>

        {/* Financial Information */}

        <Grid container size={12} spacing={2} sx={{ display: "flex", alignItems: "center" }}>
          <Grid size={12}>
            <Typography sx={{ fontWeight: 600 }}>Payment Details</Typography>
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              type="number"
              label="Total Amount"
              onFocus={(e) => e.target.select()}
              value={formData.total ?? ""}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => {
                const val = e.target.value;
                setFormData((prev) => ({
                  ...prev,
                  total: val === "" ? 0 : Number(val)
                }));
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              type="number"
              label="Advance Amount"
              onFocus={(e) => e.target.select()}
              value={formData.advance ?? ""}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => {
                const val = e.target.value;
                setFormData((prev) => ({
                  ...prev,
                  advance: val === "" ? 0 : Number(val)
                }));
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              type="number"
              onFocus={(e) => e.target.select()}
              label="Balance Amount"
              value={formData.balance ?? ""}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => {
                const val = e.target.value;
                setFormData((prev) => ({
                  ...prev,
                  balance: val === "" ? 0 : Number(val)
                }));
              }}
            />
          </Grid>
        </Grid>

        {/* Remarks */}
        <TextField
          size="small"
          variant="outlined"
          fullWidth
          multiline
          rows={3}
          label="Remarks"
          placeholder="Additional notes for lab technician or optical dispenser..."
          value={formData.remarks || ""}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => setFormData((prev) => ({ ...prev, remarks: e.target.value }))}
        />
      </Box>
    </Panel>
  );
}
